import { useMemo } from 'react'
import type { Club, Slot } from '../../shared/types'
import { dayOfMonth, isWeekend, weekdayNarrow, weekdayShort } from '../lib/format'
import { fromMinutes, toMinutes } from '../../shared/time'
import { useI18n } from '../lib/i18n'

/**
 * Clubs publish starts on incompatible grids - :00 and :30 mostly, but also
 * :15 and :45. Snapping each slot to the half-hour it begins in keeps a single
 * readable time axis; the hover card always shows the true start and end.
 */
const ROW_MINUTES = 30
const rowFor = (start: string) =>
  fromMinutes(Math.floor(toMinutes(start) / ROW_MINUTES) * ROW_MINUTES)

export interface CellRef {
  date: string
  time: string
  slots: Slot[]
  rect: DOMRect
}

interface Props {
  days: string[]
  slots: Slot[]
  clubs: Map<string, Club>
  today: string
  now: number
  /** Last date any selected club has published. Days after it are unknown. */
  coveredThrough?: string
  active: { date: string; time: string } | null
  onCell: (cell: CellRef | null) => void
  onPick: (cell: CellRef) => void
}

/** Fallback axis for an empty week, so the grid never collapses to nothing. */
const DEFAULT_ROWS = Array.from({ length: 15 }, (_, i) => `${String(8 + i).padStart(2, '0')}:00`)

export function WeekGrid({
  days,
  slots,
  clubs,
  today,
  now,
  coveredThrough,
  active,
  onCell,
  onPick,
}: Props) {
  const { t, locale } = useI18n()
  const { rows, cells } = useMemo(() => {
    const cells = new Map<string, Slot[]>()
    const times = new Set<string>()

    for (const slot of slots) {
      const row = rowFor(slot.start)
      times.add(row)
      const key = `${slot.date}|${row}`
      const bucket = cells.get(key)
      if (bucket) bucket.push(slot)
      else cells.set(key, [slot])
    }

    const rows = times.size
      ? [...times].sort((a, b) => toMinutes(a) - toMinutes(b))
      : DEFAULT_ROWS

    return { rows, cells }
  }, [slots])

  return (
    <div
      className="overflow-hidden"
      onMouseLeave={() => onCell(null)}
      role="grid"
      aria-label={t.gridLabel}
    >
      <div
        className="grid"
        style={{ gridTemplateColumns: 'var(--gutter) repeat(7, minmax(0, 1fr))' }}
      >
        {/* ---- header ---- */}
        <div
          className="sticky top-0 z-20 border-b"
          style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
        />
        {days.map((day) => {
          const isToday = day === today
          const unknown = coveredThrough !== undefined && day > coveredThrough
          return (
            <div
              key={day}
              className="sticky top-0 z-20 border-b border-l px-1 py-2 text-center"
              style={{
                background: 'var(--surface-2)',
                borderColor: 'var(--border)',
                color: isToday ? 'var(--color-brand-400)' : undefined,
              }}
            >
              <div className="text-[11px] font-medium tracking-wide uppercase">
                <span className="hidden sm:inline">{weekdayShort(day, locale)}</span>
                <span className="sm:hidden">{weekdayNarrow(day, locale)}</span>
              </div>
              {unknown && (
                <div
                  className="truncate text-[9px] leading-tight"
                  style={{ color: 'var(--text-faint)' }}
                  title={t.notOpenYet}
                >
                  {t.notOpenYet}
                </div>
              )}
              <div
                className={`nums text-[13px] leading-tight sm:text-sm ${isToday ? 'font-bold' : 'font-medium'}`}
                style={{ color: isToday ? undefined : isWeekend(day) ? 'var(--text-muted)' : undefined }}
              >
                {dayOfMonth(day, locale)}
              </div>
            </div>
          )
        })}

        {/* ---- body ---- */}
        {rows.map((time) => (
          <Row
            key={time}
            time={time}
            days={days}
            coveredThrough={coveredThrough}
            cells={cells}
            clubs={clubs}
            now={now}
            active={active}
            onCell={onCell}
            onPick={onPick}
          />
        ))}
      </div>
    </div>
  )
}

interface RowProps {
  time: string
  days: string[]
  coveredThrough?: string
  cells: Map<string, Slot[]>
  clubs: Map<string, Club>
  now: number
  active: { date: string; time: string } | null
  onCell: (cell: CellRef | null) => void
  onPick: (cell: CellRef) => void
}

function Row({ time, days, coveredThrough, cells, clubs, now, active, onCell, onPick }: RowProps) {
  const onTheHour = time.endsWith(':00')

  return (
    <>
      <div
        className="nums sticky left-0 z-10 flex items-center justify-end border-t pr-2 text-[11px] sm:pr-3 sm:text-xs"
        style={{
          background: 'var(--surface)',
          borderColor: 'var(--border)',
          color: onTheHour ? 'var(--text-muted)' : 'var(--text-faint)',
          minHeight: 'var(--row-h)',
        }}
      >
        {time}
      </div>

      {days.map((day) => {
        const slots = cells.get(`${day}|${time}`) ?? []
        return (
          <Cell
            key={`${day}|${time}`}
            date={day}
            time={time}
            slots={slots}
            clubs={clubs}
            past={slots.length > 0 && Date.parse(slots[0].startUtc) < now}
            unknown={coveredThrough !== undefined && day > coveredThrough}
            isActive={active?.date === day && active?.time === time}
            onCell={onCell}
            onPick={onPick}
          />
        )
      })}
    </>
  )
}

interface CellProps {
  date: string
  time: string
  slots: Slot[]
  clubs: Map<string, Club>
  past: boolean
  unknown: boolean
  isActive: boolean
  onCell: (cell: CellRef | null) => void
  onPick: (cell: CellRef) => void
}

function Cell({ date, time, slots, clubs, past, unknown, isActive, onCell, onPick }: CellProps) {
  const { t } = useI18n()
  const clubIds = useMemo(() => [...new Set(slots.map((s) => s.clubId))], [slots])
  const free = slots.length > 0 && !past

  // Fill deepens with the number of clubs free, so a scan of the grid reads as
  // a heatmap of "where is there real choice".
  const intensity = free ? Math.min(clubIds.length, 3) : 0
  const fill = ['transparent', 'rgb(52 211 153 / 0.16)', 'rgb(52 211 153 / 0.30)', 'rgb(52 211 153 / 0.46)'][
    intensity
  ]

  const emit = (el: HTMLElement | null) => {
    if (!el || !free) return onCell(null)
    onCell({ date, time, slots, rect: el.getBoundingClientRect() })
  }

  return (
    <button
      type="button"
      role="gridcell"
      disabled={!free}
      aria-label={
        free
          ? t.cellFree(
              date,
              time,
              slots.length,
              clubIds.map((id) => clubs.get(id)?.name ?? id).join(', '),
            )
          : t.cellEmpty(date, time)
      }
      className="relative flex items-center justify-center gap-[3px] border-t border-l transition-colors duration-100 outline-none disabled:cursor-default"
      style={{
        minHeight: 'var(--row-h)',
        borderColor: 'var(--border)',
        background: fill,
        // A day the club has not opened is struck through, so it never reads
        // as "tried and fully booked".
        backgroundImage: unknown
          ? 'repeating-linear-gradient(135deg, transparent 0 5px, var(--border) 5px 6px)'
          : undefined,
        boxShadow: isActive ? 'inset 0 0 0 2px var(--color-brand-400)' : undefined,
        opacity: past ? 0.35 : 1,
      }}
      onMouseEnter={(e) => emit(e.currentTarget)}
      onFocus={(e) => emit(e.currentTarget)}
      onClick={(e) => free && onPick({ date, time, slots, rect: e.currentTarget.getBoundingClientRect() })}
    >
      {free &&
        clubIds.slice(0, 3).map((id) => (
          <span
            key={id}
            className="size-[5px] rounded-full sm:size-1.5"
            style={{ background: clubs.get(id)?.color ?? 'var(--color-brand-400)' }}
          />
        ))}
      {free && clubIds.length > 3 && (
        <span className="nums text-[9px] leading-none" style={{ color: 'var(--text-muted)' }}>
          +{clubIds.length - 3}
        </span>
      )}
    </button>
  )
}
